// 레인 F — ARPG 모드(1차 환생) 수치 가안(docs/REBIRTH-MODES-SPEC.md 3장). 시뮬로 확정하기 전 값이며, 바꾸면 스펙 3장 F 구현 기록도 고친다.
// 게임 판정은 1m 격자, 좌표는 y 위 · 바닥 XZ · 북쪽(화면 위) = -z (levels.ts와 같은 규약).

/** 필드 한 구역: 32m 청크 5×5 = 160×160m. 청크 (cx, cz)는 x ∈ [X0 + 32·cx, +32), z ∈ [Z0 + 32·cz, +32). cz 0이 북쪽 줄 */
export const FIELD_GRID_SPEC = {
  chunk: 32,
  cols: 5,
  rows: 5,
  x0: -80,
  z0: -80,
} as const;
export const CHUNK_COUNT = FIELD_GRID_SPEC.cols * FIELD_GRID_SPEC.rows;
/** 영웅 둘레 활성 청크 반경(1 = 3×3) */
export const ACTIVE_RADIUS = 1;
/** 활성 청크 최대 수(3×3) */
export const ACTIVE_MAX = (ACTIVE_RADIUS * 2 + 1) ** 2;

export const FIELD = {
  /** 지역1 이끼 유적 필드의 결정적 씨앗 */
  seed: 20261026,
  /** 무리 크기(마리) */
  packMin: 3,
  packMax: 6,
  /** 대기 중인 필드 몹이 영웅을 알아채는 반경(m). 던전(MOBS.aggroRadius 13)보다 좁다 — 무리를 골라 당긴다 */
  aggroRadius: 9,
  /** 무리를 다 잡은 뒤 다시 나기까지(초, 세계 시간) */
  respawnSec: 45,
  /** 무리 레벨 = 영웅 레벨 ±spread(무리마다 결정적) */
  levelSpread: 2,
  /** 대기 몹이 무리 자리에서 이만큼(m) 벗어나면 자리 쪽으로 돌아선다 */
  homeLeash: 5,
  /** 영웅이 이만큼(m) 안에 있으면 그 무리를 새로 내지 않는다(눈앞에서 튀어나오지 않게) */
  spawnClearance: 11,
  /** 비활성 청크로 넘어간 무리는 영웅에게서 이만큼(m) 넘게 떨어지고 모두 대기 상태일 때만 거둔다 */
  dormantDist: 26,
  /** 필드 이벤트: 입장 뒤 첫 이벤트(초, 세계 시간), 이후 간격(초) — 스펙 "3~5분마다" */
  firstEventSec: [90, 120] as const,
  eventEverySec: [180, 300] as const,
  /** 이벤트가 열려 있는 최대 시간(초). 넘기면 무리·고블린이 사라진다 */
  eventLifeSec: 150,
  /** 이벤트 지점은 영웅에게서 이 거리(m) 사이에서 고른다(보이는 곳 밖, 걸어 갈 만한 곳) */
  eventDist: [18, 56] as const,
  /** 엘리트 무리 이벤트 확률(나머지는 보물 고블린) */
  eliteEventChance: 0.6,
  /** 엘리트 무리: 마릿수·그중 엘리트 수·레벨 가산 */
  eliteEvent: { size: 5, elites: 2, levelAdd: 2 },
  /** 보물 고블린: 맞기 전 머무는 시간(초)·맞은 뒤 도망 시간(초)·처치 골드(지역 기본 보상 단위 배수) */
  goblin: { idleSec: 90, fleeSec: 14, goldMult: 1.5 },
  /** 균열 조각: 처치 확률(일반·엘리트), 이벤트 보상 조각 수(엘리트 무리·보물 고블린) */
  shardChance: { mob: 0.03, elite: 0.25 },
  shardReward: { elite: 1, goblin: 2 },
  /** 이벤트 처치 골드(지역 기본 보상 단위 배수) */
  eventGoldMult: 1,
  /** 귀환석 곁에 가만히 이만큼(초) 서 있으면 마을로 */
  returnHoldSec: 1.5,
  /** 조각 상한(저장 검증) */
  shardCap: 999,
  /**
   * 필드 처치 EXP 목표(옛 고정값, C12부터 바닥값): 레벨당 처치 수는 shared/field/relevel.ts fieldKillsPerLevel(1회차 시간 × 0.30/0.25)이 정한다.
   * 지역1 스테이지 1 처치 EXP는 레벨과 무관해 환생 뒤 레벨업이 멈추지 않게 배율로 맞춘다
   */
  killsPerLevel: 120,
} as const;

/**
 * 필드·균열 처치 EXP 배율(StagePlan.xpDiff): 영웅 레벨 L에서 다음 레벨까지 EXP ÷ (고블린 1마리 EXP × killsPerLevel), 최소 1.
 * 처치 EXP = killXp(종류, 엘리트, r1, 스테이지 1) × 이 배율이라 엘리트(×3)·종류 차는 그대로 남는다.
 */
export function fieldXpMult(level: number, toNext: number, goblinXp: number, kills: number = FIELD.killsPerLevel): number {
  if (!(toNext > 0) || !(goblinXp > 0) || !(kills > 0)) return 1;
  return Math.max(1, Math.round((toNext / (goblinXp * kills)) * 100) / 100);
}

/** 필드 몹 레벨 배율(지역1 계단: Lv1 ×0.8 … Lv18 ×2.0, 이후 같은 기울기) */
export function fieldHpMult(level: number): number {
  return Math.round((0.8 + 0.07 * (Math.max(1, level) - 1)) * 100) / 100;
}
export function fieldDamageMult(level: number): number {
  return Math.round((0.8 + 0.055 * (Math.max(1, level) - 1)) * 100) / 100;
}

/** 균열(Rift) */
export const RIFT = {
  /** 입장 조각 수 */
  shards: 5,
  /** 층 수(무작위 2~3) */
  floorsMin: 2,
  floorsMax: 3,
  /** 몹 밀도 배율(필드 대비) */
  density: 1.5,
  /** 영웅 둘레에 살아 있게 할 몹 수(필드 평균 활성 몹 ≈ 20 × 1.5) */
  population: 30,
  /** 새 무리가 나는 거리(m, 영웅에게서) */
  spawnDist: [13, 30] as const,
  /** 무리 크기 */
  packMin: 4,
  packMax: 7,
  /** 게이지 점수: 일반·엘리트 처치, 100%에 필요한 점수 */
  gaugePoints: { mob: 1, elite: 5 },
  gaugeMax: 240,
  /** 제한 시간(초). 넘기면 보상 배율 lateRewardMult */
  limitSec: 600,
  lateRewardMult: 0.5,
  /** 균열 수호자 체력 배율(그 레벨 중간보스 체력 기준) */
  guardianHpMult: 1.15,
  /** 보상 상자: 골드(지역 기본 보상 단위 배수)·강화석 */
  chestGoldMult: 4,
  chestStones: 4,
} as const;

/** 저장 키(새 키 — 기존 save.v1·profile.v2·growth.v2·gear.v1·stage.v1 형식 불변) */
export const FIELD_SAVE_KEY = 'dungeonhero.field.v1';
export const FIELD_SAVE_VERSION = 1;
